"use client";

import {
  IMPERSONATION_BANNER_COOKIE,
  IMPERSONATION_MAX_MINUTES,
  IMPERSONATION_RETURN_PATH,
  getImpersonationElapsedMinutes,
  isPastImpersonationLimit,
  parseImpersonationBannerCookie,
} from "@punchup/data/supabase/impersonation";
import {
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { APP_BANNER_ATTRIBUTE } from "@/components/internal/users/app-banner-attribute";
import { PunchupAppBanner } from "@punchup/ui-common/punchup/primitives/feedback/PunchupAppBanner";
import { PunchupButton } from "@punchup/ui-common/punchup/primitives/buttons/PunchupButton";
import dayjs from "@punchup/utils/dayjs";
import { fetchLoginAsSecondsLeft } from "@/data/v2/client/internal/users";
import { getAuthUserDisplayName } from "@punchup/data/supabase/getAuthUserDisplayName";
import { getBrowserCookie } from "@punchup/utils/browser-cookies";
import { useSession } from "@/hooks";

const TICK_MS = 15_000;

// Cookies fire no change event, so re-read on each tick and whenever the tab
// comes back: a return in another tab hides this tab's banner.
function subscribeToBannerCookie(onChange: () => void) {
  const interval = setInterval(onChange, TICK_MS);
  window.addEventListener("focus", onChange);
  document.addEventListener("visibilitychange", onChange);
  return () => {
    clearInterval(interval);
    window.removeEventListener("focus", onChange);
    document.removeEventListener("visibilitychange", onChange);
  };
}

// The raw string, not the parsed object: a snapshot must compare equal while
// the cookie is unchanged.
const readBannerCookie = () =>
  getBrowserCookie(IMPERSONATION_BANNER_COOKIE) ?? null;
const notReadYet = () => undefined;

/**
 * The banner cookie, parsed: undefined on the server and during hydration (so
 * the first render matches the server's HTML), then null or its state, kept
 * current by subscribeToBannerCookie.
 */
function useBannerCookie() {
  const raw = useSyncExternalStore(
    subscribeToBannerCookie,
    readBannerCookie,
    notReadYet
  );
  return useMemo(
    () =>
      raw === undefined
        ? undefined
        : parseImpersonationBannerCookie(raw ?? undefined),
    [raw]
  );
}

// The return route signs the target out; the admin signs in again.
function endImpersonation() {
  window.location.assign(IMPERSONATION_RETURN_PATH);
}

/**
 * The fixed bar both banners share, pinned above the header. Clickable while a
 * Radix sheet or dialog is open: a modal sets `pointer-events: none` on
 * <body>, and the banner sits above its overlay.
 */
function BannerShell({ children }: { children: ReactNode }) {
  return (
    <div className="pointer-events-auto fixed inset-x-0 top-0 z-[60] h-[var(--app-banner-height)] overflow-hidden">
      <PunchupAppBanner status="warning">{children}</PunchupAppBanner>
    </div>
  );
}

/** `shown` is undefined until the banner knows, so the head script's guess stands. */
function useAppBannerAttribute(shown: boolean | undefined) {
  useEffect(() => {
    if (shown === undefined) return;
    document.documentElement.toggleAttribute(APP_BANNER_ATTRIBUTE, shown);
    return () => document.documentElement.removeAttribute(APP_BANNER_ATTRIBUTE);
  }, [shown]);
}

/**
 * Minutes since the impersonation started, by the server's clock once
 * `login-as/status` answers: the server's elapsed time plus the wall-clock
 * duration since it answered. A duration, so a browser clock set wrong does not
 * matter, and a sleeping computer's time counts. Until then, or while it
 * fails, the browser's clock against the cookie's start time. `serverKnown`
 * says which one it is.
 *
 * It asks again whenever the tab comes back (focus, or visible again), to
 * correct a clock that moved while the tab slept, and on every tick until the
 * first answer arrives, so one failed or rate-limited request cannot leave the
 * tab without its end.
 */
function useElapsedMinutes(startedAt: string | null) {
  const [serverBase, setServerBase] = useState<{
    startedAt: string;
    elapsedSeconds: number;
    at: number;
  } | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    let cancelled = false;
    let answered = false;
    let asking = false;
    const ask = () => {
      if (asking) return;
      asking = true;
      fetchLoginAsSecondsLeft()
        .then((secondsLeft) => {
          // null: no readable marker on the server; nothing to count against.
          answered = true;
          if (cancelled || secondsLeft === null) return;
          setServerBase({
            startedAt,
            elapsedSeconds: IMPERSONATION_MAX_MINUTES * 60 - secondsLeft,
            at: dayjs().valueOf(),
          });
        })
        .catch(() => {
          // Asked again on the next tick; middleware also ends it on the next
          // navigation.
        })
        .finally(() => {
          asking = false;
        });
    };
    const onTick = () => {
      if (!answered) ask();
      setTick((tick) => tick + 1);
    };
    const onReturn = () => {
      if (document.visibilityState === "visible") ask();
    };
    ask();
    const interval = setInterval(onTick, TICK_MS);
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [startedAt]);

  if (!startedAt) return { minutes: 0, serverKnown: false };
  // A base from an earlier impersonation does not count for this one.
  if (serverBase?.startedAt !== startedAt) {
    return {
      minutes: getImpersonationElapsedMinutes(startedAt, dayjs()),
      serverKnown: false,
    };
  }
  const seconds =
    serverBase.elapsedSeconds + (dayjs().valueOf() - serverBase.at) / 1000;
  return { minutes: Math.floor(seconds / 60), serverKnown: true };
}

/**
 * Tells a super admin, on every page, that login-as has them signed in as
 * someone else, for how long, and lets them end it.
 *
 * Shows while the impersonation marker names the current session's user, so a
 * marker left behind by a sign-out never puts the banner on another account.
 * While the session loads it shows from the marker alone ("another user"), so
 * the space the head script reserved is never an empty band; it counts and
 * ends nothing until the session confirms the target. Pinned above the fixed
 * header through the app-banner slot (`packages/styles/CLAUDE.md` → "Fixed top
 * chrome").
 *
 * At IMPERSONATION_MAX_MINUTES by the server's clock it sends the browser to
 * the return route, so an idle open tab ends too, within one tick (15 s) of
 * the limit; middleware covers navigation and reopened tabs. It never ends on
 * the browser's clock alone: a fast clock would end every impersonation at
 * once.
 *
 * When the marker goes away while the banner shows (the admin ended the
 * impersonation in another tab), it reloads the page: this tab still holds the
 * target's session and data in memory, and hiding the warning alone would
 * leave them on screen.
 *
 * The minutes left are hidden from screen readers (the banner is a live
 * region, so a changing count would be re-read every minute); they hear the
 * end time instead.
 */
export default function ImpersonationBanner() {
  const session = useSession();
  const impersonation = useBannerCookie();

  const target =
    impersonation && session?.user.id === impersonation.targetUserId
      ? { user: session.user, startedAt: impersonation.startedAt }
      : null;
  const sessionLoading = Boolean(impersonation) && session === undefined;
  const shown = target !== null || sessionLoading;

  useAppBannerAttribute(impersonation === undefined ? undefined : shown);
  const elapsed = useElapsedMinutes(target?.startedAt ?? null);
  const expired =
    elapsed.serverKnown && isPastImpersonationLimit(elapsed.minutes);

  // Set once this tab is leaving for the return route, so the marker it clears
  // there does not also trigger the reload below.
  const ending = useRef(false);
  const end = () => {
    ending.current = true;
    endImpersonation();
  };

  useEffect(() => {
    if (!expired) return;
    ending.current = true;
    endImpersonation();
  }, [expired]);

  // Only a confirmed target counts as shown: a marker for another account
  // also shows while the session loads, and must not reload every page.
  const wasTarget = useRef(false);
  const isTarget = target !== null;
  useEffect(() => {
    if (isTarget) {
      wasTarget.current = true;
    } else if (wasTarget.current && !ending.current) {
      window.location.reload();
    }
  }, [isTarget]);

  if (!shown) return null;

  const name =
    (target && getAuthUserDisplayName(target.user)) ?? "another user";
  const minutesLeft = Math.max(0, IMPERSONATION_MAX_MINUTES - elapsed.minutes);
  const endsAt =
    target &&
    dayjs(target.startedAt)
      .add(IMPERSONATION_MAX_MINUTES, "minute")
      .format("h:mm A");

  return (
    <BannerShell>
      <span className="flex min-w-0 items-center gap-3">
        <span className="min-w-0 truncate">
          <span className="font-semibold">
            You&rsquo;re signed in as {name}.
          </span>
          <span className="hidden md:inline">
            {" "}
            Anything you do happens on this account.
          </span>
          {target && (
            <>
              {" "}
              <span aria-hidden="true">{minutesLeft} min left.</span>
              <span className="sr-only">Ends at {endsAt}.</span>
            </>
          )}
        </span>
        <PunchupButton
          variant="secondary"
          size="sm"
          compact
          className="shrink-0"
          onClick={end}
        >
          <span className="md:hidden">Sign out</span>
          <span className="hidden md:inline">Sign out of this account</span>
        </PunchupButton>
      </span>
    </BannerShell>
  );
}

/**
 * What the root layout shows if ImpersonationBanner throws: the admin must not
 * lose the only visible sign of the impersonation, or the way to end it. No
 * session or network: just the cookie and a plain link (no prefetch).
 */
export function ImpersonationBannerFallback() {
  const impersonation = useBannerCookie();
  const shown =
    impersonation === undefined ? undefined : impersonation !== null;

  useAppBannerAttribute(shown);

  if (!shown) return null;

  return (
    <BannerShell>
      You&rsquo;re signed in as another user.{" "}
      <a href={IMPERSONATION_RETURN_PATH}>Sign out of this account</a>
    </BannerShell>
  );
}
